/**
 * Central data-testid registry.
 *
 * Conventions (codifying the existing de-facto pattern):
 * - kebab-case, feature-prefixed: `{feature}-{element}` (e.g. `sprint-search-input`)
 * - dynamic ids: template on the entity id: `` `${TESTIDS.x}-${id}` `` → `org-members-row-abc123`
 * - design system components accept a `data-testid` prop directly (Button, Input, …)
 *
 * New testids should be added here and referenced from components, so AI agents and
 * Playwright specs have one source of truth (see ai-agentic-testing/testids.md).
 */
export const TESTIDS = {
  // Auth (/login)
  AUTH_EMAIL_INPUT: 'auth-email-input',
  AUTH_EMAIL_CONTINUE_BTN: 'auth-email-continue-btn',
  AUTH_CODE_INPUT: 'auth-code-input',
  AUTH_CODE_VERIFY_BTN: 'auth-code-verify-btn',
  AUTH_NOT_INVITED_ERROR: 'auth-not-invited-error',

  // Shared wizard footer (sprint creation + reflection wizards)
  WIZARD_NEXT_BTN: 'wizard-next-btn',
  WIZARD_BACK_BTN: 'wizard-back-btn',

  // Gamer / game
  GAME_CENTER_ENTER_LOBBY_BTN: 'game-center-enter-lobby-btn',
  /** Upcoming Activities hero CTA — THE canonical game entry for agents */
  UPCOMING_ENTER_LOBBY_BTN: 'enter-team-lobby-btn',
  /** Upcoming Activities hero CTA for an AI debrief — the debrief entry for agents */
  UPCOMING_ENTER_REFLECTION_LOBBY_BTN: 'enter-reflection-lobby-btn',
  UPCOMING_JOIN_SPRINT_CALL_BTN: 'join-sprint-call-btn',
  /** AI debrief page: continue to the next part, disabled while Aria speaks, leave once every part is done */
  AI_COACH_CONTINUE_BTN: 'ai-coach-continue-btn',
  ACTIVITY_JOIN_BTN: 'activity-join-btn',
  PLAY_GAME_BACK_BTN: 'play-game-back-btn',
  PLAY_GAME_FULLSCREEN_BTN: 'play-game-fullscreen-btn',
  /** Blocking modal shown on phones/tablets — game activities are desktop-only */
  GAME_DESKTOP_ONLY_MODAL: 'game-desktop-only-modal',
  GAME_DESKTOP_ONLY_CONFIRM_BTN: 'game-desktop-only-confirm-btn',

  // Whereby — one live call per user
  /** Shown instead of the call when the user's call is open on another tab/device */
  WHEREBY_CALL_BLOCKED_PANEL: 'whereby-call-blocked-panel',
  /** The ONLY control that ends a call on another tab/device (explicit user confirmation) */
  WHEREBY_CALL_TAKEOVER_BTN: 'whereby-call-takeover-btn',
  WHEREBY_CALL_TAKEOVER_CANCEL_BTN: 'whereby-call-takeover-cancel-btn',
  /** Offered only when a call came back from a drop with nobody's sound or picture */
  WHEREBY_CALL_RECONNECT_BTN: 'whereby-call-reconnect-btn',
  WHEREBY_SCREENSHARE_STAGE: 'whereby-screenshare-stage',
  SPRINT_CALL_PAGE: 'sprint-call-page',

  // Cookie banner
  COOKIE_ACCEPT_BTN: 'cookie-accept-btn',
  COOKIE_REJECT_BTN: 'cookie-reject-btn',

  /** Slow-connection notice — at most one per browser session, dismissible */
  SLOW_NETWORK_NOTIFICATION: 'slow-network-notification',
  /** In the call but not on the AI debrief page once the debrief has run a minute — opens the page, dismissible */
  DEBRIEF_STARTED_NOTIFICATION: 'debrief-started-notification',

  // Sprint creation wizard
  /** Dynamic: `${SPRINT_ACTIVITY_CARD}-${activityId}` */
  SPRINT_ACTIVITY_CARD: 'sprint-activity-card',
  SPRINT_ADD_PARTICIPANT_BTN: 'sprint-add-participant-btn',
  /** Switches from the organization member picker to the name + email form */
  SPRINT_ADD_NEW_PARTICIPANT_BTN: 'sprint-add-new-participant-btn',
  SPRINT_PARTICIPANT_NAME_INPUT: 'sprint-participant-name-input',
  SPRINT_PARTICIPANT_EMAIL_INPUT: 'sprint-participant-email-input',
  SPRINT_TEAM_AUTO_ASSIGN_BTN: 'sprint-team-auto-assign-btn',
  SPRINT_TEAM_CREATE_BTN: 'sprint-team-create-btn',
  /** Bulk language dropdown trigger in the teams toolbar */
  SPRINT_TEAM_LANG_BULK_TRIGGER: 'sprint-team-lang-bulk-trigger',
  SPRINT_TEAM_LANG_APPLY_ALL_BTN: 'sprint-team-lang-apply-all-btn',
  /** Dynamic: `${SPRINT_TEAM_LANG_TRIGGER}-${teamId}` — per-team language dropdown */
  SPRINT_TEAM_LANG_TRIGGER: 'sprint-team-lang-trigger',
  /** Dynamic: `${SPRINT_TEAM_REMOVE_BTN}-${teamId}` */
  SPRINT_TEAM_REMOVE_BTN: 'sprint-team-remove-btn',
  /** Loop-count <Select> on a game activity (GameActivityLoopSettings) */
  SPRINT_GAME_LOOPS_SELECT: 'sprint-game-loops-select',
  /** Step 2 "Customize" — switches purpose to CUSTOM so activities become add/removable */
  SPRINT_CUSTOMIZE_BTN: 'sprint-customize-btn',
  /** Dynamic: `${SPRINT_ACTIVITY_REMOVE_BTN}-${activityId}` (custom purpose only) */
  SPRINT_ACTIVITY_REMOVE_BTN: 'sprint-activity-remove-btn',
  /** Calendar modal quick buttons — "Next slot" is the agents' default */
  CALENDAR_NEXT_SLOT_BTN: 'calendar-next-slot-btn',
  CALENDAR_NOW_BTN: 'calendar-now-btn',
  WIZARD_SAVE_DRAFT_BTN: 'wizard-save-draft-btn',
  WIZARD_DELETE_DRAFT_BTN: 'wizard-delete-draft-btn',

  // Reflection wizard
  REFLECTION_NAME_INPUT: 'reflection-name-input',
  REFLECTION_SEND_BTN: 'reflection-send-btn',
  REFLECTION_SEND_CONFIRM_BTN: 'reflection-send-confirm-btn',
  REFLECTION_SEND_CANCEL_BTN: 'reflection-send-cancel-btn',

  // Gamer onboarding
  /** Offered when the next onboarding step lives on the coach page rather than in a modal. */
  ONBOARDING_COACH_GOALS_HANDOVER: 'onboarding-coach-goals-handover',
  ONBOARDING_COACH_GOALS_CONFIRM_BTN: 'onboarding-coach-goals-confirm-btn',

  // Sidebar / profile
  SIDEBAR_PROFILE_TRIGGER: 'sidebar-profile-trigger',

  // App assistant (Aria)
  ASSISTANT_OPEN_BTN: 'assistant-open-btn',
  ASSISTANT_PANEL: 'assistant-panel',
  ASSISTANT_CLOSE_BTN: 'assistant-close-btn',
  ASSISTANT_INPUT: 'assistant-input',
  ASSISTANT_SEND_BTN: 'assistant-send-btn',
  ASSISTANT_VOICE_TOGGLE_BTN: 'assistant-voice-toggle-btn',
  ASSISTANT_VOICE_MUTE_BTN: 'assistant-voice-mute-btn',
  ASSISTANT_VOICE_END_BTN: 'assistant-voice-end-btn',
  ASSISTANT_MESSAGE: 'assistant-message',
  ASSISTANT_CONVERSATION: 'assistant-conversation',
  ASSISTANT_QUICK_ACTION: 'assistant-quick-action',
  ASSISTANT_ESCALATE_BTN: 'assistant-escalate-btn',
  ASSISTANT_REFRESH_BTN: 'assistant-refresh-btn',
  /** The panel header doubles as the grab area that moves the whole widget. */
  ASSISTANT_PANEL_DRAG_HANDLE: 'assistant-panel-drag-handle',
  ASSISTANT_RESET_POSITION_BTN: 'assistant-reset-position-btn',
  ASSISTANT_MINIMIZE_BTN: 'assistant-minimize-btn',
  /** Open support request: who holds the panel, and the two ways out of it. */
  ASSISTANT_SUPPORT_BANNER: 'assistant-support-banner',
  ASSISTANT_SUPPORT_SWITCH_BTN: 'assistant-support-switch-btn',
  ASSISTANT_SUPPORT_RESOLVE_BTN: 'assistant-support-resolve-btn',
  ASSISTANT_SUPPORT_RESOLVE_PROMPT: 'assistant-support-resolve-prompt',
  ASSISTANT_SUPPORT_RESOLVE_CONFIRM_BTN: 'assistant-support-resolve-confirm-btn',
  /** What Aria says beside the bubble, e.g. on the latest session feedback. */
  ASSISTANT_GREETING: 'assistant-greeting',
  ASSISTANT_GREETING_OPEN_BTN: 'assistant-greeting-open-btn',
  ASSISTANT_GREETING_DISMISS_BTN: 'assistant-greeting-dismiss-btn',

  // Aria interaction mode — shared by the floating assistant and the 1:1 coach
  ARIA_MODE_SWITCH: 'aria-mode-switch',
  ARIA_MODE_VOICE_BTN: 'aria-mode-voice-btn',
  ARIA_MODE_SUBTITLES_BTN: 'aria-mode-subtitles-btn',
  ARIA_MODE_TEXT_BTN: 'aria-mode-text-btn',
  ARIA_LANGUAGE_BTN: 'aria-language-btn',
  ARIA_LANGUAGE_OPTION: 'aria-language-option',
  ARIA_VOICE_PRIMER: 'aria-voice-primer',
  ARIA_VOICE_PRIMER_START_BTN: 'aria-voice-primer-start-btn',
  ARIA_VOICE_PRIMER_TEXT_BTN: 'aria-voice-primer-text-btn',
  ARIA_SUBTITLES: 'aria-subtitles',
  ARIA_BREATHING_BULB: 'aria-breathing-bulb',

  // 1:1 coach page
  COACH_PAGE: 'coach-page',
  COACH_CONTINUE_ONBOARDING: 'coach-continue-onboarding',
  COACH_GOAL_ROW: 'coach-goal-row',
  COACH_GOAL_EDIT_BTN: 'coach-goal-edit-btn',
  COACH_GOAL_DELETE_BTN: 'coach-goal-delete-btn',
  /** Mark achieved / put back — an achieved goal stops occupying one of the three active slots. */
  COACH_GOAL_COMPLETE_BTN: 'coach-goal-complete-btn',
  COACH_GOAL_REOPEN_BTN: 'coach-goal-reopen-btn',
  COACH_GOAL_EDIT_INPUT: 'coach-goal-edit-input',
  COACH_GOAL_SAVE_BTN: 'coach-goal-save-btn',
  COACH_GOAL_DELETE_CONFIRM_BTN: 'coach-goal-delete-confirm-btn',
  /** Why a change to this goal was refused, on the row the click came from. */
  COACH_GOAL_FAILURE: 'coach-goal-failure',
  COACH_SIDE_PANEL: 'coach-side-panel',
  COACH_SIDE_PANEL_TOGGLE: 'coach-side-panel-toggle',
  /** The folded panel. Distinct from the expanded one so a test can tell the two states apart. */
  COACH_SIDE_PANEL_RAIL: 'coach-side-panel-rail',
  COACH_OPEN_FEEDBACK_BTN: 'coach-open-feedback-btn',
  /** What a turn could not say itself — e.g. a goal the active-goal cap refused to write. */
  COACH_NOTICE: 'coach-notice',
  COACH_FEEDBACK_MODAL: 'coach-feedback-modal',

  // Facilitator Control Tower
  CONTROL_TOWER_END_FOR_TEAM_CONFIRM_BTN: 'control-tower-end-for-team-confirm-btn',
  CONTROL_TOWER_SEND_TEAM_CONFIRM_BTN: 'control-tower-send-team-confirm-btn',

  // Next-activity reminder (5 min / 1 min before start)
  NEXT_ACTIVITY_REMINDER_MODAL: 'next-activity-reminder-modal',
  NEXT_ACTIVITY_REMINDER_GO_BTN: 'next-activity-reminder-go-btn',
  NEXT_ACTIVITY_REMINDER_CLOSE_BTN: 'next-activity-reminder-close-btn',

  // Organization members — leader role
  ORG_CHANGE_ROLE_CONFIRM_BTN: 'org-change-role-confirm-btn',
  ORG_LEADER_DEMOTION_WARNING: 'org-leader-demotion-warning',
  ORG_LEADER_REMOVAL_WARNING: 'org-leader-removal-warning',
  ORG_MEMBER_MANAGE_PEOPLE_BTN: 'org-member-manage-people-btn',
  LEADER_SCOPE_MODAL: 'leader-scope-modal',
  LEADER_SCOPE_SEARCH: 'leader-scope-search',
  LEADER_SCOPE_SAVE_BTN: 'leader-scope-save-btn',
  MY_PEOPLE_PAGE: 'my-people-page',
  /** A figure at the top of My people that opens what it counts, such as the list of their people. */
  MY_PEOPLE_KEY_METRIC_BTN: 'my-people-key-metric-btn',
  MY_PEOPLE_PEOPLE_MODAL: 'my-people-people-modal',
  MY_PEOPLE_TEAM_PICKER: 'my-people-team-picker',
  MY_PEOPLE_STATE_PANEL: 'my-people-state-panel',
  MY_PEOPLE_TALK_TO_ARIA_BTN: 'my-people-talk-to-aria-btn',
  COACH_FOCUS_SWITCH: 'coach-focus-switch',

  // Platform tour (gamer)
  /** Dashboard header: "Take the tour", or "Resume tour" after it was paused in this visit */
  PLATFORM_TOUR_ENTRY_BTN: 'platform-tour-entry-btn',
  PLATFORM_TOUR_ENTRY_DISMISS_BTN: 'platform-tour-entry-dismiss-btn',
  PLATFORM_TOUR_START_BTN: 'platform-tour-start-btn',
  PLATFORM_TOUR_MAYBE_LATER_BTN: 'platform-tour-maybe-later-btn',
  PLATFORM_TOUR_STOP_PANEL: 'platform-tour-stop-panel',
  /** Next stop, or "Finish tour" on the last one */
  PLATFORM_TOUR_NEXT_BTN: 'platform-tour-next-btn',
  PLATFORM_TOUR_BACK_BTN: 'platform-tour-back-btn',
  PLATFORM_TOUR_SKIP_BTN: 'platform-tour-skip-btn',
  PLATFORM_TOUR_PAUSE_BTN: 'platform-tour-pause-btn',
  PLATFORM_TOUR_CONTINUE_BTN: 'platform-tour-continue-btn',
  PLATFORM_TOUR_REPLAY_BTN: 'platform-tour-replay-btn',
  /** Account Settings: start the tour again from its first stop */
  PLATFORM_TOUR_SETTINGS_REPLAY_BTN: 'platform-tour-settings-replay-btn'
} as const

/**
 * Every design-system DropdownMenu item renders `data-testid="dropdown-item-<item.id>"`
 * (DropdownMenu.tsx). Use this to target them — e.g. the sidebar logout is
 * `dropdownItemTestId('logout')` → `dropdown-item-logout`.
 */
export const dropdownItemTestId = (itemId: string): string => `dropdown-item-${itemId}`

/** Options inside any TeamLanguageSelector dropdown: `team-language-option-en`, `-sv`, … */
export const teamLanguageOptionTestId = (languageCode: string): string =>
  `team-language-option-${languageCode.toLowerCase()}`

export type TestId = (typeof TESTIDS)[keyof typeof TESTIDS]
